import React, { useState } from "react";
import SidebarAdmin from "./SidebarAdmin";
import AdminNavbar from "./AdminNavbar";
import PostAdminButton from "../Assets/PostAdminButton";
import useAboutUs from "../Hooks/useAboutUs";
import AdminAboutUsTable from "./AdminAboutUsTable";
import AboutUsModal from "./AboutUsModal";
import ModalConfirmation from "../Assets/ModalConfirmation";
import { AboutUsEntry } from "../Types/Types";
import AboutUsEditModal from "./AboutUsEditModal";
import AdminSearchBar from "../Assets/AdminSearchBar";
import { useTranslation } from "react-i18next";

interface AdminAboutUsProps {
  lang: string;
}

const AdminAboutUs: React.FC<AdminAboutUsProps> = ({ lang }) => {
  const { t } = useTranslation()

  const [showForm, setShowForm] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedAboutUs, setSelectedAboutUs] = useState<AboutUsEntry | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [toDeleteId, setToDeleteId] = useState<string | null>(null);

  const {
    aboutUsData,
    loading,
    error,
    createAboutUs,
    updateAboutUs,
    deleteAboutUs,
    searchQuery,
    setSearchQuery,
    setSortBy,
    setSortOrder,
    reorder,
    success,
    setSuccess
  } = useAboutUs();

  const handleSearch = (query: string) => {
    setSearchQuery(query); // Update the search query state
  };

  // Deletion logic
  const handleDeleteRequest = (id: string) => {
    setToDeleteId(id);
    setModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (toDeleteId) {
      await deleteAboutUs(toDeleteId);
      setToDeleteId(null);
    }
    setModalOpen(false);
  };

  const handleDeleteCancel = () => {
    setToDeleteId(null);
    setModalOpen(false);
  };

  return (
    <div className="min-h-screen bg-gray-100 flex">
      <div className="w-80">
        <SidebarAdmin />
      </div>
      <div className="flex-1 flex flex-col">
        <AdminNavbar />
        <div className="flex-1 py-12 px-48 space-y-12 font-montserrat">
          <h1 className="text-4xl font-bold text-brand-800">
            {t(`navbar.aboutUs`)}

          </h1>
          <div className="flex justify-between items-center space-x-4">
            <PostAdminButton onClick={() => setShowForm(true)} />
            <AdminSearchBar searchQuery={searchQuery} onSearch={handleSearch} />
          </div>
          <AdminAboutUsTable
            aboutUsData={aboutUsData}
            loading={loading}
            error={error}
            onDelete={handleDeleteRequest}
            onEdit={(entry) => {
              setSelectedAboutUs(entry);
              setShowEditModal(true);
            }}
            onSortChange={(field, order) => {
              setSortBy(field);
              setSortOrder(order);
            }}
            onReorder={async (newOrder: AboutUsEntry[]) => {
              // Map the array to include the position index
              const reorderedWithPosition = newOrder.map((item, index) => ({
                _id: item._id,
                position: index + 1,  // or just index if your backend expects 0-based
              }));

              await reorder(reorderedWithPosition);
            }}
            lang={lang}
            success={success}
            setSuccess={setSuccess}
          />
        </div>
      </div>
      <AboutUsModal
        isOpen={showForm}
        onClose={() => setShowForm(false)}
        onSubmit={async (formData: FormData) => {
          await createAboutUs(formData);
          setShowForm(false);
        }}
      />
      {showEditModal && selectedAboutUs && (
        <AboutUsEditModal
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          onSubmit={async (id, updatedData) => {
            await updateAboutUs(id, updatedData);
            setShowEditModal(false);
            setSelectedAboutUs(null);
          }}
          initialData={selectedAboutUs}
          lang={lang}
        />
      )}
      <ModalConfirmation
        isOpen={modalOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
      />
    </div>
  );
};

export default AdminAboutUs;
